import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { FaShoppingCart, FaHandHoldingUsd, FaGlobe, FaCheckCircle, FaArrowRight, FaArrowLeft, FaCoins, FaChartLine, FaPercent, FaClock, FaLink, FaCreditCard, FaCheck, FaLock, FaCheckDouble, FaExclamationCircle, FaLightbulb, FaRocket, FaBolt, FaTrophy, FaLayerGroup } from 'react-icons/fa';
import { TbCoinBitcoin } from 'react-icons/tb';
import './CreateOrder.css';

import { apiFetch } from '../services/apiClient';

// Moneda local por país (mismo set que el resto de la app)
const COUNTRY_CURRENCY = {
  CR: { code: 'CRC', sym: '₡' }, CO: { code: 'COP', sym: '$' }, VE: { code: 'VES', sym: 'Bs' },
  MX: { code: 'MXN', sym: '$' }, AR: { code: 'ARS', sym: '$' }, PE: { code: 'PEN', sym: 'S/' },
  CL: { code: 'CLP', sym: '$' }, GT: { code: 'GTQ', sym: 'Q' }, US: { code: 'USD', sym: '$' },
};

// Spread con signo (descuento = negativo) ←→ {priceType, spread}
const signedFrom = (priceType, spread) =>
  priceType === 'discount' ? -Math.abs(parseFloat(spread) || 0)
  : priceType === 'premium' ?  Math.abs(parseFloat(spread) || 0) : 0;

function fromSigned(signed) {
  if (Math.abs(signed) < 0.05) return { priceType: 'market', spread: '0' };
  if (signed < 0) return { priceType: 'discount', spread: String(Math.round(Math.abs(signed) * 10) / 10) };
  return { priceType: 'premium', spread: String(Math.round(signed * 10) / 10) };
}

const AMOUNT_CHIPS = [100, 500, 1000, 5000];

const signedLabel = (s) => Math.abs(s) < 0.05 ? 'TRM' : s > 0 ? `+${Math.round(s * 10) / 10}%` : `${Math.round(s * 10) / 10}%`;

// Guía según rol: qué significa el precio para comprador vs vendedor
function priceHint(priceType, type) {
  if (priceType === 'market') return 'Tu oferta irá al precio actual del mercado (TRM, 0% de spread).';
  if (priceType === 'discount') return type === 'sell'
    ? 'Vendés por debajo del mercado → atraés compradores y cerrás más rápido.'
    : 'Pagás por debajo del mercado → te conviene a vos, pero puede tardar en aparecer un vendedor.';
  return type === 'sell'
    ? 'Vendés por encima del mercado → ganás más por USDT, pero puede tardar.'
    : 'Pagás por encima del mercado → atraés vendedores y cerrás más rápido.';
}

const NETWORKS = [
  { value: 'BSC',      label: 'BNB Smart Chain', icon: FaCoins, color: '#f0b90b' },
  { value: 'TRON',     label: 'TRON',            icon: FaCoins, color: '#e53935' },
  { value: 'Ethereum', label: 'Ethereum',        icon: FaCoins, color: '#627eea' },
  { value: 'Solana',   label: 'Solana',          icon: FaCoins, color: '#9945ff' },
  { value: 'Litecoin', label: 'Litecoin',        icon: FaCoins, color: '#f7931a' },
  { value: 'Bitcoin',  label: 'Bitcoin',         icon: TbCoinBitcoin, color: '#f7931a' },
];

const PAYMENT_METHODS = [
  'Transferencia bancaria', 'PayPal', 'Wink',
  'Nequi', 'Daviplata', 'Zinli', 'SINPE Móvil',
  'Zelle', 'PIX', 'Mercado Pago', 'Yape', 'SPEI',
];

const RESPONSE_TIMES = [
  { value: '1',   label: '1 minuto'   },
  { value: '2',   label: '2 minutos'  },
  { value: '5',   label: '5 minutos'  },
  { value: '10',  label: '10 minutos' },
  { value: '15',  label: '15 minutos' },
  { value: '30',  label: '30 minutos' },
  { value: '45',  label: '45 minutos' },
  { value: '60',  label: '1 hora'     },
  { value: '90',  label: '1.5 horas'  },
  { value: '120', label: '2 horas'    },
];

const INITIAL = {
  type: 'buy', amount: '', minAmount: '', maxAmount: '',
  responseTime: '5', priceType: 'market', spread: '',
  networks: ['BSC'],
  paymentMethods: ['Transferencia bancaria'],
};

const STEPS = [
  { id: 1, label: 'Tipo',       icon: FaShoppingCart },
  { id: 2, label: 'Monto',      icon: FaCoins },
  { id: 3, label: 'Red y Pago', icon: FaGlobe },
  { id: 4, label: 'Confirmar',  icon: FaCheckCircle },
];

function CreateOrder({ onCreate }) {
  const navigate      = useNavigate();
  const location      = useLocation();
  const { user }      = useAuth();
  const isVerified    = user?.verified === true;
  const isVerifiedRef = useRef(isVerified);

  const [formData, setFormData] = useState(INITIAL);
  const [step, setStep]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [market, setMarket]     = useState([]);   // ofertas públicas → insight de competencia
  const [rate, setRate]         = useState(null);  // fiat por USD (moneda local)

  const cc = COUNTRY_CURRENCY[user?.country] || null;

  const successDataRef = useRef(null);

  // Traer ofertas del mercado para comparar precio
  useEffect(() => {
    (async () => {
      try { const res = await apiFetch('/api/offers'); if (res.ok) setMarket(await res.json()); } catch {}
    })();
  }, []);

  // Tasa fiat para mostrar el precio en moneda local
  useEffect(() => {
    if (!cc) return;
    fetch('https://open.er-api.com/v6/latest/USD')
      .then(r => r.json())
      .then(d => { if (d?.rates?.[cc.code]) setRate(d.rates[cc.code]); })
      .catch(() => {});
  }, [cc]);

  // Spread con signo de la oferta actual + precio efectivo de 1 USDT (estable ≈ $1)
  const signedSpread = signedFrom(formData.priceType, formData.spread);
  const effUsd       = 1 * (1 + signedSpread / 100);

  // Insight: cómo está el mercado para mi tipo (compra/venta) y dónde quedo
  const marketStats = useMemo(() => {
    const peers = market.filter(o => o.type === formData.type);
    if (peers.length === 0) return null;
    const spreads = peers.map(o => Number(o.spread) || 0);
    const best = formData.type === 'sell' ? Math.min(...spreads) : Math.max(...spreads); // venta: más barato gana; compra: más caro gana
    const avg  = spreads.reduce((a, b) => a + b, 0) / spreads.length;
    const better = peers.filter(o => formData.type === 'sell'
      ? (Number(o.spread) || 0) < signedSpread
      : (Number(o.spread) || 0) > signedSpread).length;
    return { count: peers.length, best, avg, rank: better + 1 };
  }, [market, formData.type, signedSpread]);

  // Pone un precio que le gana (apenas) al mejor del mercado
  const useCompetitivePrice = () => {
    if (!marketStats) return;
    const target = formData.type === 'sell' ? marketStats.best - 0.1 : marketStats.best + 0.1;
    setFormData(p => ({ ...p, ...fromSigned(target) }));
    setError('');
  };

  const timerRef = useRef(null);
  useEffect(() => {
    if (submitted) {
      timerRef.current = setTimeout(() => {
        navigate('/', {
          state: {
            offerCreated: true,
            offerType:    successDataRef.current?.type,
            offerAmount:  successDataRef.current?.amount,
          },
        });
      }, 4000);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [submitted, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type   = params.get('type');

    if (params.get('amount')) {
      setFormData(prev => ({
        ...prev,
        type:           params.get('type')        ?? 'buy',
        amount:         params.get('amount')       ?? '',
        minAmount:      params.get('minAmount')    ?? '',
        maxAmount:      params.get('maxAmount')    ?? '',
        spread:         params.get('spread')       ?? '',
        responseTime:   params.get('responseTime') ?? '5',
        networks:       params.get('network')      ? [params.get('network')] : ['BSC'],
        paymentMethods: params.get('payment')      ? [params.get('payment')] : ['Transferencia bancaria'],
        priceType:      parseFloat(params.get('spread') ?? '0') === 0 ? 'market'
                        : parseFloat(params.get('spread') ?? '0') > 0 ? 'premium' : 'discount',
      }));
    }

    if (type === 'sell' && !isVerifiedRef.current) {
      setFormData(prev => ({ ...prev, type: 'sell' }));
      setError('sell_blocked');
      setStep(1);
    }
  }, [location.search]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleNetwork = (value) => {
    setFormData(prev => {
      const has = prev.networks.includes(value);
      if (has && prev.networks.length === 1) return prev;
      return { ...prev, networks: has ? prev.networks.filter(n => n !== value) : [...prev.networks, value] };
    });
  };

  const togglePayment = (value) => {
    setFormData(prev => {
      const has = prev.paymentMethods.includes(value);
      if (has && prev.paymentMethods.length === 1) return prev;
      return { ...prev, paymentMethods: has ? prev.paymentMethods.filter(p => p !== value) : [...prev.paymentMethods, value] };
    });
  };

  const validateStep2 = () => {
    const amount    = parseFloat(formData.amount)    || 0;
    const minAmount = parseFloat(formData.minAmount) || 0;
    const maxAmount = parseFloat(formData.maxAmount) || 0;
    const spread    = parseFloat(formData.spread)    || 0;
    if (!formData.amount || amount <= 0)                          return 'El monto total debe ser mayor a 0.';
    if (minAmount < 0)                                            return 'El mínimo por operación no puede ser menor a 0.';
    if (minAmount > 0 && maxAmount > 0 && minAmount > maxAmount)  return 'El mínimo no puede ser mayor al máximo.';
    if (maxAmount > 0 && maxAmount > amount)                      return `El máximo (${maxAmount}) no puede superar el monto total (${amount}).`;
    if (minAmount > 0 && minAmount > amount)                      return `El mínimo (${minAmount}) no puede superar el monto total (${amount}).`;
    if (formData.priceType !== 'market' && (!formData.spread || spread <= 0)) return 'Ingresá un porcentaje de spread mayor a 0.';
    return '';
  };

  const handleNext = () => {
    if (step === 1 && formData.type === 'sell' && !isVerified) {
      setError('sell_blocked');
      return;
    }
    if (step === 2) {
      const err = validateStep2();
      if (err) { setError(err); return; }
    }
    setError('');
    setStep(s => Math.min(s + 1, 4));
  };

  const handleBack = () => {
    setError('');
    setStep(s => Math.max(s - 1, 1));
  };

  const handlePublish = async () => {
    if (formData.type === 'sell' && !isVerified) {
      setError('sell_blocked');
      return;
    }
    setLoading(true);
    setError('');
    // Una oferta por cada red seleccionada (antes solo se guardaba la primera)
    const networks = formData.networks.length ? formData.networks : ['BSC'];
    try {
      let created = 0, lastErr = '';
      for (const net of networks) {
        const res = await apiFetch('/api/offers', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            network:       net,
            paymentMethod: formData.paymentMethods.join(', '),
            spread:        signedSpread,
          }),
        });
        if (res.ok) { created++; continue; }
        const err = await res.json().catch(() => ({}));
        if (err.requiresInvite) { setError('sell_blocked'); setLoading(false); return; }
        lastErr = err.message || 'Error al publicar oferta';
        break;
      }
      if (created === 0) throw new Error(lastErr || 'Error al publicar oferta');

      if (onCreate) onCreate(formData);

      successDataRef.current = { type: formData.type, amount: formData.amount, count: created, networks: networks.slice(0, created) };
      setLoading(false);
      setSubmitted(true);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const getPriceLabel = () => {
    if (formData.priceType === 'market')   return 'Mercado (0%)';
    if (formData.priceType === 'discount') return `-${formData.spread}% descuento`;
    if (formData.priceType === 'premium')  return `+${formData.spread}% premium`;
    return '—';
  };

  const maxAllowed = parseFloat(formData.amount) || 0;

  if (submitted && successDataRef.current) {
    return (
      <div className="co-wrapper">
        <div className="co-success">
          <div className="co-success-icon">
            <FaCheckDouble />
          </div>
          <h2>{successDataRef.current.count > 1 ? `¡${successDataRef.current.count} ofertas publicadas!` : '¡Oferta publicada!'}</h2>
          <p>
            Tu oferta de{' '}
            <strong>{successDataRef.current.type === 'buy' ? 'Compra' : 'Venta'}</strong>
            {' '}por{' '}
            <strong>USDT {Number(successDataRef.current.amount).toLocaleString()}</strong>
            {successDataRef.current.count > 1
              ? <> ya está visible en el Marketplace en <strong>{successDataRef.current.count} redes</strong> ({successDataRef.current.networks.join(', ')}).</>
              : <> ya está visible en el Marketplace.</>}
          </p>
          <p style={{ color: 'var(--mk-text-tertiary)', fontSize: '0.85rem', marginTop: '8px' }}>
            Redirigiendo al Marketplace en 4 segundos...
          </p>
          <button
            type="button"
            onClick={() => navigate('/', { state: { offerCreated: true } })}
            className="co-btn-success"
          >
            Ir al Marketplace ahora <FaArrowRight style={{ marginLeft: '6px' }} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="co-wrapper">
      <div className="co-card">

        <div className="co-header">
          <h2 className="co-title"><FaRocket style={{ marginRight: '12px' }} /> Crear nueva oferta</h2>
          <p className="co-subtitle">Completa los pasos para publicar tu oferta P2P en el Marketplace.</p>
        </div>

        {!isVerified && (
          <div className="co-beta-banner">
            <span className="co-beta-icon"><FaLock /></span>
            <div className="co-beta-text">
              <strong>Bitplify está en Beta cerrada</strong>
              <span>Podés crear ofertas de <strong>compra</strong> libremente. Para <strong>vender</strong> necesitás un código de invitación.</span>
            </div>
          </div>
        )}

        {isVerified && (
          <div className="co-verified-banner">
            <span><FaCheckDouble /></span>
            <span>Sos un vendedor verificado — podés crear ofertas de compra y venta.</span>
          </div>
        )}

        <div className="co-stepper">
          {STEPS.map((s, i) => {
            const StepIcon = s.icon;
            return (
              <React.Fragment key={s.id}>
                <div className={`co-step ${step === s.id ? 'active' : ''} ${step > s.id ? 'done' : ''}`}>
                  <div className="co-step-circle">
                    {step > s.id ? <FaCheck /> : <StepIcon />}
                  </div>
                  <span className="co-step-label">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`co-step-line ${step > s.id ? 'done' : ''}`} />}
              </React.Fragment>
            );
          })}
        </div>

        <div>

          {/* ── PASO 1: Tipo ── */}
          {step === 1 && (
            <div className="co-section">
              <h3 className="co-section-title"><FaShoppingCart style={{ marginRight: '8px' }} /> ¿Qué quieres hacer?</h3>
              <p className="co-hint">Selecciona si quieres <strong>comprar</strong> o <strong>vender</strong> crypto.</p>
              <div className="co-type-grid">
                <button type="button"
                  className={`co-type-btn ${formData.type === 'buy' ? 'selected-buy' : ''}`}
                  onClick={() => { setFormData(p => ({ ...p, type: 'buy' })); setError(''); }}
                >
                  <span className="co-type-icon"><FaShoppingCart /></span>
                  <strong>Comprar Crypto</strong>
                  <span className="co-type-desc">Quiero adquirir USDT u otra crypto con dinero fiat.</span>
                </button>
                <button type="button"
                  className={`co-type-btn ${formData.type === 'sell' ? 'selected-sell' : ''} ${!isVerified ? 'locked' : ''}`}
                  onClick={() => { setFormData(p => ({ ...p, type: 'sell' })); if (!isVerified) setError('sell_blocked'); else setError(''); }}
                >
                  <span className="co-type-icon">{!isVerified ? <FaLock /> : <FaHandHoldingUsd />}</span>
                  <strong>Vender Crypto {!isVerified && <span className="co-lock-tag">Solo por invitación</span>}</strong>
                  <span className="co-type-desc">
                    {!isVerified ? 'Requiere código de invitación. Contactanos para obtener acceso.' : 'Quiero cambiar mi crypto por dinero fiat.'}
                  </span>
                </button>
              </div>
              {error === 'sell_blocked' && (
                <div className="co-sell-blocked">
                  <span><FaLock /></span>
                  <div>
                    <strong>Acceso de vendedor requerido</strong>
                    <p>Bitplify está en beta cerrada. Para publicar ofertas de venta necesitás un código de invitación.</p>
                  </div>
                </div>
              )}
              {error !== 'sell_blocked' && (
                <div className="co-info-box">
                  <span><FaLightbulb /></span>
                  <p>Al publicar una oferta de <strong>{formData.type === 'buy' ? 'Compra' : 'Venta'}</strong>, otros traders podrán contactarte.</p>
                </div>
              )}
            </div>
          )}

          {/* ── PASO 2: Monto y Precio ── */}
          {step === 2 && (
            <div className="co-section">
              <h3 className="co-section-title"><FaCoins style={{ marginRight: '8px' }} /> Monto y Precio</h3>

              <div className="co-field">
                <label className="co-label"><TbCoinBitcoin style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Monto total disponible (USDT) <span className="co-required">*</span></label>
                <div className="co-input-wrap">
                  <span className="co-input-prefix">USDT</span>
                  <input type="number" name="amount" placeholder="Ej: 500" value={formData.amount} onChange={handleChange} min="1" className="co-input" />
                </div>
                <div className="co-chips">
                  {AMOUNT_CHIPS.map(v => (
                    <button key={v} type="button" className={`co-chip ${String(v) === String(formData.amount) ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, amount: String(v) }))}>USDT {v.toLocaleString()}</button>
                  ))}
                </div>
                <span className="co-field-hint">Monto total que tenés disponible para esta oferta.</span>
              </div>

              <div className="co-row">
                <div className="co-field">
                  <label className="co-label"><FaChartLine style={{ marginRight: '6px', color: '#00b248' }} /> Mínimo por operación</label>
                  <div className="co-input-wrap">
                    <span className="co-input-prefix">USDT</span>
                    <input
                      type="number" name="minAmount" placeholder="Ej: 20"
                      value={formData.minAmount} onChange={handleChange}
                      className={`co-input ${parseFloat(formData.minAmount) < 0 ? 'input-error' : ''}`}
                      min="0"
                    />
                  </div>
                  {parseFloat(formData.minAmount) < 0 && (
                    <span className="co-field-error"><FaExclamationCircle style={{ marginRight: '4px' }} /> No puede ser menor a 0</span>
                  )}
                  {parseFloat(formData.minAmount) > 0 && maxAllowed > 0 && parseFloat(formData.minAmount) > maxAllowed && (
                    <span className="co-field-error"><FaExclamationCircle style={{ marginRight: '4px' }} /> No puede superar el monto total (USDT {maxAllowed})</span>
                  )}
                </div>
                <div className="co-field">
                  <label className="co-label"><FaChartLine style={{ marginRight: '6px', color: '#f4511e' }} /> Máximo por operación</label>
                  <div className="co-input-wrap">
                    <span className="co-input-prefix">USDT</span>
                    <input
                      type="number" name="maxAmount"
                      placeholder={maxAllowed > 0 ? `Máx. ${maxAllowed}` : 'Ej: 200'}
                      value={formData.maxAmount} onChange={handleChange}
                      className={`co-input ${parseFloat(formData.maxAmount) > maxAllowed && maxAllowed > 0 ? 'input-error' : ''}`}
                      min="1"
                    />
                  </div>
                  {parseFloat(formData.maxAmount) > maxAllowed && maxAllowed > 0 && (
                    <span className="co-field-error"><FaExclamationCircle style={{ marginRight: '4px' }} /> No puede superar el monto total (USDT {maxAllowed})</span>
                  )}
                  {parseFloat(formData.minAmount) > 0 && parseFloat(formData.maxAmount) > 0 && parseFloat(formData.minAmount) > parseFloat(formData.maxAmount) && (
                    <span className="co-field-error"><FaExclamationCircle style={{ marginRight: '4px' }} /> El mínimo no puede ser mayor al máximo</span>
                  )}
                  {maxAllowed > 0 && String(formData.maxAmount) !== String(maxAllowed) && (
                    <button type="button" className="co-mini-link" onClick={() => setFormData(p => ({ ...p, maxAmount: String(maxAllowed) }))}>Usar el máximo (USDT {maxAllowed.toLocaleString()})</button>
                  )}
                </div>
              </div>

              <div className="co-field">
                <label className="co-label"><FaPercent style={{ marginRight: '6px' }} /> Precio / Spread <span className="co-required">*</span></label>
                <div className="co-price-type">
                  <button type="button" className={`co-price-btn ${formData.priceType === 'discount' ? 'selected discount' : ''}`} onClick={() => setFormData(p => ({ ...p, priceType: 'discount', spread: '' }))}><FaChartLine /> Descuento</button>
                  <button type="button" className={`co-price-btn ${formData.priceType === 'market'   ? 'selected market' : ''}`} onClick={() => setFormData(p => ({ ...p, priceType: 'market',   spread: '0' }))}><FaChartLine /> Mercado</button>
                  <button type="button" className={`co-price-btn ${formData.priceType === 'premium'  ? 'selected premium' : ''}`} onClick={() => setFormData(p => ({ ...p, priceType: 'premium',  spread: '' }))}><FaChartLine /> Premium</button>
                </div>
                {formData.priceType !== 'market' && (
                  <div className="co-input-wrap" style={{ marginTop: '8px' }}>
                    <span className="co-input-prefix" style={{ color: formData.priceType === 'discount' ? '#00b248' : '#f4511e' }}>
                      {formData.priceType === 'discount' ? '−%' : '+%'}
                    </span>
                    <input type="number" name="spread" placeholder="Ej: 2" value={formData.spread} onChange={handleChange} className="co-input" min="0.1" max="20" step="0.1" />
                  </div>
                )}
                <span className="co-field-hint">{priceHint(formData.priceType, formData.type)}</span>

                {/* Insight de competencia en vivo */}
                {marketStats && (
                  <div className="co-market-insight">
                    <div className="co-mi-head"><FaBolt style={{ marginRight: 6 }} /> Mercado ahora · {formData.type === 'buy' ? 'compras' : 'ventas'} ({marketStats.count})</div>
                    <div className="co-mi-rows">
                      <div className="co-mi-cell"><span>Mejor</span><strong>{signedLabel(marketStats.best)}</strong></div>
                      <div className="co-mi-cell"><span>Promedio</span><strong>{signedLabel(marketStats.avg)}</strong></div>
                      <div className="co-mi-cell">
                        <span>Tu oferta</span>
                        <strong className={`co-mi-pos ${marketStats.rank === 1 ? 'best' : marketStats.rank <= Math.ceil(marketStats.count * 0.3) ? 'good' : 'meh'}`}>
                          {marketStats.rank === 1 ? '🏆 La mejor' : marketStats.rank <= Math.ceil(marketStats.count * 0.3) ? `Competitiva #${marketStats.rank}` : `#${marketStats.rank} de ${marketStats.count + 1}`}
                        </strong>
                      </div>
                    </div>
                    <button type="button" className="co-mi-btn" onClick={useCompetitivePrice}>
                      <FaTrophy style={{ marginRight: 6 }} /> Usar precio competitivo
                    </button>
                  </div>
                )}
              </div>

              <div className="co-field">
                <label className="co-label"><FaClock style={{ marginRight: '6px' }} /> Tiempo de respuesta</label>
                <select name="responseTime" value={formData.responseTime} onChange={handleChange} className="co-select">
                  {RESPONSE_TIMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <span className="co-field-hint">Cuánto tardás como máximo en confirmar una operación.</span>
              </div>

              {/* Preview en vivo de la oferta */}
              {parseFloat(formData.amount) > 0 && (() => {
                const amt        = parseFloat(formData.amount) || 0;
                const totalUsd   = amt * effUsd;
                const perLocal   = rate && cc ? `${cc.sym}${(effUsd * rate).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : null;
                const totalLocal = rate && cc ? `${cc.sym}${(totalUsd * rate).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null;
                return (
                  <div className="co-preview">
                    <div className="co-preview-head"><FaLightbulb style={{ marginRight: '6px' }} /> Cómo se verá tu oferta</div>
                    <div className="co-preview-rows">
                      <div className="co-preview-row"><span>{formData.type === 'buy' ? 'Querés comprar' : 'Querés vender'}</span><strong>USDT {amt.toLocaleString()}</strong></div>
                      <div className="co-preview-row"><span>Precio por USDT</span><strong className="co-eff-price">${effUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{perLocal && <span className="co-local"> ≈ {perLocal}</span>}</strong></div>
                      <div className="co-preview-row"><span>Spread</span><strong className={`co-preview-price ${formData.priceType}`}>{getPriceLabel()}</strong></div>
                      <div className="co-preview-row co-preview-total"><span>{formData.type === 'buy' ? 'Pagarías por todo' : 'Recibirías por todo'}</span><strong>${totalUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}{totalLocal && <span className="co-local"> ≈ {totalLocal}</span>}</strong></div>
                    </div>
                    <div className="co-preview-note">{priceHint(formData.priceType, formData.type)}</div>
                  </div>
                );
              })()}

              {error && error !== 'sell_blocked' && (
                <div className="co-error-box"><FaExclamationCircle /> {error}</div>
              )}
            </div>
          )}

          {/* ── PASO 3: Red y Pago ── */}
          {step === 3 && (
            <div className="co-section">
              <h3 className="co-section-title"><FaGlobe style={{ marginRight: '8px' }} /> Red Blockchain y Método de Pago</h3>
              <div className="co-field">
                <label className="co-label"><FaLink style={{ marginRight: '6px' }} /> Redes Blockchain <span className="co-required">*</span><span className="co-multi-hint"> — podés seleccionar varias</span></label>
                <div className="co-network-grid">
                  {NETWORKS.map(n => {
                    const NetworkIcon = n.icon;
                    return (
                      <button key={n.value} type="button"
                        className={`co-network-btn ${formData.networks.includes(n.value) ? 'selected' : ''}`}
                        onClick={() => toggleNetwork(n.value)}
                        style={{ borderColor: formData.networks.includes(n.value) ? n.color : undefined }}
                      >
                        <span style={{ color: n.color }}><NetworkIcon /></span><span>{n.label}</span>
                        {formData.networks.includes(n.value) && <span className="co-check"><FaCheck /></span>}
                      </button>
                    );
                  })}
                </div>
                <span className="co-field-hint">Seleccionadas: {formData.networks.join(', ')}</span>
              </div>
              <div className="co-field">
                <label className="co-label"><FaCreditCard style={{ marginRight: '6px' }} /> Métodos de pago <span className="co-required">*</span><span className="co-multi-hint"> — podés seleccionar varios</span></label>
                <div className="co-payment-grid">
                  {PAYMENT_METHODS.map(m => (
                    <button key={m} type="button"
                      className={`co-payment-btn ${formData.paymentMethods.includes(m) ? 'selected' : ''}`}
                      onClick={() => togglePayment(m)}
                    >
                      {formData.paymentMethods.includes(m) && <FaCheck style={{ marginRight: '4px' }} />}{m}
                    </button>
                  ))}
                </div>
                <span className="co-field-hint">Seleccionados: {formData.paymentMethods.join(', ')}</span>
              </div>
            </div>
          )}

          {/* ── PASO 4: Confirmar ── */}
          {step === 4 && (
            <div className="co-section">
              <h3 className="co-section-title"><FaCheckCircle style={{ marginRight: '8px' }} /> Confirmar oferta</h3>
              <div className="co-summary">
                <div className="co-summary-item"><span className="co-summary-label">Tipo</span><span className={`co-summary-value ${formData.type === 'buy' ? 'type-buy' : 'type-sell'}`}>{formData.type === 'buy' ? <FaShoppingCart style={{ marginRight: '6px' }} /> : <FaHandHoldingUsd style={{ marginRight: '6px' }} />}{formData.type === 'buy' ? 'Compra' : 'Venta'}</span></div>
                <div className="co-summary-item"><span className="co-summary-label">Monto total</span><span className="co-summary-value"><TbCoinBitcoin style={{ marginRight: '6px' }} />USDT {Number(formData.amount || 0).toLocaleString()}</span></div>
                {formData.minAmount && <div className="co-summary-item"><span className="co-summary-label">Límites</span><span className="co-summary-value">USDT {formData.minAmount} — {formData.maxAmount || '∞'}</span></div>}
                <div className="co-summary-item"><span className="co-summary-label">Precio</span><span className="co-summary-value"><FaChartLine style={{ marginRight: '6px' }} />{getPriceLabel()}</span></div>
                <div className="co-summary-item"><span className="co-summary-label">Precio por USDT</span><span className="co-summary-value">${effUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{rate && cc ? ` ≈ ${cc.sym}${(effUsd * rate).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : ''}</span></div>
                <div className="co-summary-item"><span className="co-summary-label">Redes</span><span className="co-summary-value"><FaLink style={{ marginRight: '6px' }} /> {formData.networks.join(', ')}</span></div>
                <div className="co-summary-item"><span className="co-summary-label">Pagos</span><span className="co-summary-value" style={{ fontSize: '0.8rem' }}><FaCreditCard style={{ marginRight: '6px' }} /> {formData.paymentMethods.join(', ')}</span></div>
                <div className="co-summary-item"><span className="co-summary-label">Respuesta</span><span className="co-summary-value"><FaClock style={{ marginRight: '6px' }} /> {RESPONSE_TIMES.find(t => t.value === formData.responseTime)?.label ?? `${formData.responseTime} min`}</span></div>
              </div>
              <div className="co-info-box green">
                <span>{formData.networks.length > 1 ? <FaLayerGroup /> : <FaCheckCircle />}</span>
                <p>{formData.networks.length > 1
                  ? <>Se publicarán <strong>{formData.networks.length} ofertas</strong> (una por red: {formData.networks.join(', ')}), visibles en el Marketplace al instante.</>
                  : 'Tu oferta será visible inmediatamente en el Marketplace para todos los traders.'}</p>
              </div>
              {error && error !== 'sell_blocked' && (
                <div className="co-error-box"><FaExclamationCircle /> {error}</div>
              )}
            </div>
          )}

          <div className="co-nav">
            {step > 1 && (
              <button type="button" className="co-btn-back" onClick={handleBack}><FaArrowLeft /> Atrás</button>
            )}
            {step < 4 ? (
              <button type="button" className="co-btn-next" onClick={handleNext} disabled={step === 2 && !formData.amount}>
                Siguiente <FaArrowRight style={{ marginLeft: '6px' }} />
              </button>
            ) : (
              <button type="button" className="co-btn-submit" onClick={handlePublish} disabled={loading}>
                {loading ? '⏳ Publicando...' : <>
                  <FaRocket style={{ marginRight: '8px' }} />
                  Publicar oferta
                </>}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default CreateOrder;
